import { memo, useMemo } from 'react';
import { Empty, Skeleton } from 'antd';
import { Panel } from './Panel';
import { useWidgetData, currencyFmt } from './shared';
import type { BranchControlConfig } from '../../types/panelConfig';

interface Props {
  config: BranchControlConfig;
  summarySourceId: number | null | undefined;
  kasaSourceId: number | null | undefined;
  parameters?: Record<string, string>;
}

const numberFmt = new Intl.NumberFormat('tr-TR', { maximumFractionDigits: 0 });

interface Branch {
  magaza: string;
  ciro: number;
  fis: number;
  pos: number;
  nakit: number;
  veresiye: number;
  platform: number;
}

interface KasaRow {
  magaza: string;
  tur: string;
  kasaAd: string;
  giris: number;
  cikis: number;
  net: number;
}

function num(v: unknown): number {
  return Number(v) || 0;
}

// One <details> accordion per şube: header shows magaza + dönem ciro; the open
// body holds 3 KPIs (Ciro/Fiş/Sepet ort.), a POS/Nakit payment-mix bar, and a
// kasa table. summary rows = branches; kasa rows joined by magaza.
// F4: POS bar (996009, belge-cipali) ile ayni ekrandaki POS KPI (996008,
// tahsilat-cipali) ve kasa tablosunun POS grubu (996010, tahsilat-cipali) FARKLI
// olculerdir -- sercanozan'da bugun tesadufen ortusuyorlar (iki telafi edici fark
// birbirini goturuyor), ilk ayristiklarinda kullanicinin tek basvurusu bu daldaki
// sorunun kendisiydi. Varsayilan subtitle bunu ACIKCA soyler; config kendi metnini
// verirse (DB'den) o kullanilir -- burada varsayilan, DB degisikligi GEREKTIRMEZ.
const VARSAYILAN_ALTBASLIK =
  'Ödeme dağılımı satış belgesi tarihine göre; POS KPI tahsilat tarihine göre hesaplanır.';

function BranchControlWidgetImpl({ config, summarySourceId, kasaSourceId, parameters }: Props) {
  const { groupField = 'magaza' } = config;
  const summary = useWidgetData(summarySourceId, `branch-summary-${config.summaryReportCode}`, parameters);
  const kasa = useWidgetData(kasaSourceId, `branch-kasa-${config.kasaReportCode}`, parameters);

  const branches = useMemo<Branch[]>(() => {
    return (summary.data ?? [])
      .map((r) => ({
        magaza: String(r[groupField] ?? ''),
        ciro: num(r.ciro),
        fis: num(r.fis_sayisi),
        pos: num(r.pos_tutar),
        nakit: num(r.nakit_tutar),
        veresiye: num(r.veresiye),
        platform: num(r.platform),
      }))
      .sort((a, b) => b.ciro - a.ciro);
  }, [summary.data, groupField]);

  // Sekiz kiraci hala eski 996009 seklini donduruyor (veresiye/platform kolonlari
  // yok). Kolon YOKLUGUNU (deger degil) kontrol ediyoruz: yeni sekilde bu alanlar
  // 0 da olabilir (o gun veresiye satisi olmamis bir sube gibi) ve o durumda yine
  // gosterilmeli. Eski kiracida alan hic gelmiyor -> cubuklar gizli kalir.
  const hasVeresiyePlatform = useMemo(
    () => (summary.data ?? []).some((r) => 'veresiye' in r || 'platform' in r),
    [summary.data],
  );

  // Ayni gerekce: 'tur' kolonu gelmeden once kasa tablosu tek gruptu, hicbir
  // baslik satiri yoktu. Deger degil, kolon YOKLUGUNU kontrol ediyoruz — sekiz
  // kiracida grup basligi hic gorunmemeli (deger her zaman 'Kasa' varsayilsa
  // bile).
  const hasTur = useMemo(
    () => (kasa.data ?? []).some((r) => 'tur' in r),
    [kasa.data],
  );

  const kasaByBranch = useMemo<Map<string, KasaRow[]>>(() => {
    const map = new Map<string, KasaRow[]>();
    for (const r of kasa.data ?? []) {
      const magaza = String(r[groupField] ?? '');
      const row: KasaRow = {
        magaza,
        // 'tur' kolonu gelmeden once (sekiz kiraci) her satir kasa hareketiydi.
        tur: String(r.tur ?? 'Kasa'),
        kasaAd: String(r.kasa_ad ?? ''),
        giris: num(r.giris),
        cikis: num(r.cikis),
        net: num(r.net_hareket),
      };
      const list = map.get(magaza) ?? [];
      list.push(row);
      map.set(magaza, list);
    }
    return map;
  }, [kasa.data, groupField]);

  const isLoading = summary.isLoading || kasa.isLoading;

  return (
    <div data-testid="branch-control-widget">
      <Panel title={config.title} subtitle={config.subtitle ?? VARSAYILAN_ALTBASLIK}>
        {isLoading ? (
          <Skeleton active paragraph={{ rows: 4 }} />
        ) : branches.length === 0 ? (
          <Empty description="Veri yok" style={{ padding: 40 }} />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {branches.map((b, i) => {
              const sepet = b.fis > 0 ? b.ciro / b.fis : 0;
              // Odeme karisimi dort bilesenle kapaniyor: ciro = POS + Nakit + Veresiye + Platform.
              const bilesenToplami = b.pos + b.nakit + b.veresiye + b.platform;
              // F5: SQL kapanisi (996009'un kendi guard'i) burada TEKRAR
              // dogrulanmiyor -- bu panel onun yerine gecmez. Ama kapanis
              // gecmisde bile bozulursa (yeni bir kiraci, degisen bir predicate)
              // ekranin bunu SESSIZCE yutmasini engelliyoruz: yuzdeler mumkunse
              // ciro'dan turetilir (dogru payda budur), ve ciro ile bilesen
              // toplami 1 kurustan fazla ayrisirsa gorunur bir uyari basilir.
              const total = b.ciro > 0 ? b.ciro : bilesenToplami;
              const kapanmiyor = b.ciro > 0 && Math.abs(bilesenToplami - b.ciro) > 0.01;
              const pct = (v: number) => (total > 0 ? Math.round((v / total) * 100) : 0);
              const rows = kasaByBranch.get(b.magaza) ?? [];
              // 'tur' kolonu yoksa (sekiz kiraci) TEK grup, baslik satirsiz — eskisiyle
              // birebir. Varsa Kasa/POS diye iki grup, Kasa her zaman once.
              const kasaGruplari = hasTur
                ? ['Kasa', 'POS'].map((t) => ({ tur: t as string | null, rows: rows.filter((k) => k.tur === t) }))
                : [{ tur: null as string | null, rows }];
              return (
                <details
                  key={b.magaza}
                  open={i === 0}
                  style={{
                    border: '1px solid var(--color-border)',
                    borderRadius: 4,
                    overflow: 'hidden',
                  }}
                >
                  <summary
                    style={{
                      display: 'flex',
                      alignItems: 'baseline',
                      justifyContent: 'space-between',
                      gap: 8,
                      padding: '10px 12px',
                      cursor: 'pointer',
                      listStyle: 'none',
                      background: 'var(--color-bg-subtle, #f8fafc)',
                    }}
                  >
                    <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--color-text-primary)' }}>
                      {b.magaza}
                    </span>
                    <span
                      className="tnum"
                      style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-text-primary)' }}
                    >
                      {currencyFmt.format(b.ciro)}
                    </span>
                  </summary>

                  <div style={{ padding: 12, display: 'flex', flexDirection: 'column', gap: 16 }}>
                    {/* 3 KPI */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
                      <Kpi label="Ciro" value={currencyFmt.format(b.ciro)} />
                      <Kpi label="Fiş" value={numberFmt.format(b.fis)} />
                      <Kpi label="Sepet ort." value={currencyFmt.format(sepet)} />
                    </div>

                    {/* POS / Nakit / Veresiye / Platform payment mix */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                      {kapanmiyor && (
                        <div
                          data-testid="odeme-kapanma-uyarisi"
                          style={{
                            fontSize: 11.5,
                            fontWeight: 600,
                            color: 'var(--color-negative)',
                            background: 'var(--color-border-light, #eef1f5)',
                            border: '1px solid var(--color-negative)',
                            borderRadius: 4,
                            padding: '6px 8px',
                          }}
                        >
                          ⚠ Ödeme bileşenleri toplamı ({currencyFmt.format(bilesenToplami)}) ciro (
                          {currencyFmt.format(b.ciro)}) ile uyuşmuyor — yüzdeler ciro üzerinden hesaplandı.
                        </div>
                      )}
                      <Bar label="POS" amount={b.pos} pct={pct(b.pos)} fill="var(--color-primary)" />
                      <Bar label="Nakit" amount={b.nakit} pct={pct(b.nakit)} fill="var(--color-positive)" />
                      {hasVeresiyePlatform && (
                        <>
                          {/* Veresiye: tahsil EDILMEDI, alacak olarak duruyor. */}
                          <Bar label="Veresiye" amount={b.veresiye} pct={pct(b.veresiye)} fill="var(--color-warning)" />
                          {/* Platform: tahsil EDILDI ama araci uzerinden (bu kiracida
                              Trendyol Yemek / Yemeksepeti); Veresiye ile karistirilmamali. */}
                          <Bar label="Platform" amount={b.platform} pct={pct(b.platform)} fill="var(--color-info, #4a6ea8)" />
                        </>
                      )}
                    </div>

                    {/* Kasa table, tur'e (Kasa/POS) gore iki gruba bolunmus */}
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
                      <thead>
                        <tr style={{ color: 'var(--color-text-muted)', textAlign: 'left' }}>
                          <th style={thStyle}>Kasa</th>
                          <th style={{ ...thStyle, textAlign: 'right' }}>Giriş</th>
                          <th style={{ ...thStyle, textAlign: 'right' }}>Çıkış</th>
                          <th style={{ ...thStyle, textAlign: 'right' }}>Net</th>
                        </tr>
                      </thead>
                      {kasaGruplari.map(({ tur, rows: grup }) => {
                        if (grup.length === 0) return null;
                        return (
                          <tbody key={tur ?? 'all'} {...(tur ? { 'data-testid': `kasa-grup-${tur}` } : {})}>
                            {tur && (
                              <tr>
                                <td
                                  colSpan={4}
                                  style={{
                                    ...tdStyle,
                                    paddingTop: 10,
                                    fontWeight: 700,
                                    fontSize: 11,
                                    color: 'var(--color-text-muted)',
                                    textTransform: 'uppercase',
                                  }}
                                >
                                  {tur}
                                </td>
                              </tr>
                            )}
                            {grup.map((k) => (
                              <tr key={`${b.magaza}-${tur ?? 'all'}-${k.kasaAd}`} style={{ borderTop: '1px solid var(--color-border)' }}>
                                <td style={tdStyle}>{k.kasaAd}</td>
                                <td className="tnum" style={{ ...tdStyle, textAlign: 'right' }}>
                                  {currencyFmt.format(k.giris)}
                                </td>
                                <td className="tnum" style={{ ...tdStyle, textAlign: 'right' }}>
                                  {currencyFmt.format(k.cikis)}
                                </td>
                                <td
                                  className="tnum"
                                  style={{ ...tdStyle, textAlign: 'right', fontWeight: 600 }}
                                >
                                  {currencyFmt.format(k.net)}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        );
                      })}
                    </table>
                  </div>
                </details>
              );
            })}
          </div>
        )}
      </Panel>
    </div>
  );
}

const thStyle: React.CSSProperties = { padding: '6px 8px', fontWeight: 600, fontSize: 11 };
const tdStyle: React.CSSProperties = { padding: '6px 8px', color: 'var(--color-text-primary)' };

function Kpi({ label, value }: { label: string; value: string }) {
  return (
    <div
      style={{
        border: '1px solid var(--color-border)',
        borderRadius: 6,
        padding: '8px 10px',
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
      }}
    >
      <span
        style={{
          fontSize: 10.5,
          fontWeight: 600,
          color: 'var(--color-text-muted)',
          textTransform: 'uppercase',
          letterSpacing: 0.5,
        }}
      >
        {label}
      </span>
      <span
        className="tnum"
        style={{ fontSize: 16, fontWeight: 700, color: 'var(--color-text-primary)' }}
      >
        {value}
      </span>
    </div>
  );
}

function Bar({ label, amount, pct, fill }: { label: string; amount: number; pct: number; fill: string }) {
  // F2: negatif bir kova (orn. iade-agirlikli kisa pencerede veresiye) meydana
  // gelebilir ve spec (Ö11/K5) bunun GIZLENMEMESI gerektigini soyluyor. CSSOM
  // `width: -10%`i REDDEDER (gecersiz deger -> `auto`'ya duser), boylece dolgu
  // 8px'lik parcanin TAMAMINI kaplardi -- en KUCUK (negatif) tutar kendi
  // TERSI gibi gorunurdu. Genislik burada Math.max(0, pct) ile kenetlenir
  // (asla `auto`'ya dusmez); etiket YINE de gercek isaretli degeri gosterir,
  // ve track (dolgunun arkasindaki iz) negatifken kendine ozgu bir cizgili
  // desenle isaretlenir -- boylece "bos cubuk" ile "olculmedi/sifir" ayni
  // gorunmez, anomali okunabilir kalir.
  const negatif = pct < 0;
  const genislik = Math.max(0, pct);
  return (
    <div data-testid={`odeme-bar-${label}`}>
      <div style={{ display: 'flex', alignItems: 'baseline', marginBottom: 6 }}>
        <span style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>{label}</span>
        <span
          className="tnum"
          style={{
            marginLeft: 'auto',
            fontSize: 13,
            fontWeight: 600,
            color: negatif ? 'var(--color-negative)' : 'var(--color-text-primary)',
          }}
        >
          {currencyFmt.format(amount)}
        </span>
        <span
          className="tnum"
          style={{
            marginLeft: 10,
            fontSize: 12.5,
            fontWeight: negatif ? 700 : 400,
            color: negatif ? 'var(--color-negative)' : 'var(--color-text-muted)',
            minWidth: 38,
            textAlign: 'right',
          }}
        >
          %{pct}
        </span>
      </div>
      <div
        data-testid={`odeme-bar-${label}-track`}
        style={{
          height: 8,
          borderRadius: 5,
          background: negatif
            ? 'repeating-linear-gradient(135deg, var(--color-negative) 0 4px, var(--color-border-light, #eef1f5) 4px 8px)'
            : 'var(--color-border-light, #eef1f5)',
          overflow: 'hidden',
        }}
      >
        <div
          data-testid={`odeme-bar-${label}-fill`}
          style={{
            width: `${genislik}%`,
            height: '100%',
            borderRadius: 5,
            background: negatif ? 'var(--color-negative)' : fill,
            transition: 'width .3s ease',
          }}
        />
      </div>
    </div>
  );
}

export const BranchControlWidget = memo(BranchControlWidgetImpl);
